import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { endOfWeek, startOfWeek } from 'date-fns'
import { IFuelBatchModal } from 'pages/FuelManagement/AddBatchModal'
import { IFuelDispenseModal } from 'pages/FuelManagement/AddDispenseModal'
import { IFuelSuppliersModal } from 'pages/FuelManagement/Suppliers/FuelSuppliersModal'
import { IFuelTypesModal } from 'pages/FuelManagement/Types/FuelTypesModal'
import { toast } from 'react-toastify'
import {
  IBatch,
  ICreateBatchRequest,
  ICreateDispenseRequest,
  ICreateFuelTaskRequest,
  IDispense,
  IFuelConsumptionOverTime,
  IFuelDashboardAmounts,
  IFuelDashboardTanks,
  IFuelLossResponse,
  IFuelSupplier,
  IFuelTank,
  IFuelType,
  IGetAllFuelMachinesSingleResponse,
  IGetFuelAlertsResponse,
  IPostPatchFuelSupplierRequest,
  IPostPatchFuelTankRequest,
  IPostPatchFuelTypeRequest,
  IUpdateFuelLossCaseStatusRequest,
  IAddFuelTankWaterCheckRequest,
  IAddFuelTankCleaningRequest,
} from 'types/api/fuelManagementApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import { FuelDispenseMethod, FuelTankClass } from 'types/fuelEnums.interface'
import {
  IFuelMangementSlice,
  IFuelTaskState,
  ITanksModal,
} from 'types/fuelManagement.interface'
import { baseUrl } from 'utils/axios'
import { formatDateWithoutTimezone, formatParams, getParams, handleErrors } from 'utils/helpers'

const initialState: IFuelMangementSlice = {
  dashboardAmounts: null,
  dashboardTanks: null,
  isLoading: false,
  dashboardAmountsLoading: false,
  dashboardTanksLoading: false,
  isLoadingAlerts: false,
  machines: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
  fuelLosses: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
  fuelConsumption: null,
  alerts: [],
  batches: {
    data: [],
    totalCount: 0,
    totalPages: 0,
    isLoading: false,
  },
  dispenses: {
    data: [],
    totalCount: 0,
    totalPages: 0,
    isLoading: false,
  },
  tanks: {
    data: [],
    totalCount: 0,
    totalPages: 0,
    isLoading: false,
  },
  types: {
    data: [],
    totalCount: 0,
    totalPages: 0,
    isLoading: false,
  },
  suppliers: {
    data: [],
    totalCount: 0,
    totalPages: 0,
    isLoading: false,
  },
  tasks: {
    isLoading: false,
  },
}

export const getFuelAlerts = createAsyncThunk(
  'fuel/getFuelAlerts',
  async (params: any, thunkAPI) => {
    try {
      params = !params
        ? formatParams({
            dateFrom: startOfWeek(new Date(), { weekStartsOn: 1 }),
            dateTo: endOfWeek(new Date(), { weekStartsOn: 1 }),
          })
        : formatParams(params)
      const resp: AxiosResponse<IGetFuelAlertsResponse> = await baseUrl.get(
        'fuel/management/dashboard/fuel/alert',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getBatches = createAsyncThunk('fuel/getBatches', async (params: any, thunkAPI) => {
  try {
    params = !params
      ? formatParams({
          page: 1,
          limit: 5,
          dateFrom: startOfWeek(new Date(), { weekStartsOn: 1 }),
          dateTo: endOfWeek(new Date(), { weekStartsOn: 1 }),
        })
      : formatParams(params)
    const resp: AxiosResponse<IPaginatedResponse<IBatch>> = await baseUrl.get(
      'fuel/management/batch',
      { params },
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getLastBatches = createAsyncThunk('fuel/getLastBatches', async (_, thunkAPI) => {
  try {
    const resp: AxiosResponse<IBatch[]> = await baseUrl.get('fuel/management/batch/last?last=5')
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getLastDispenses = createAsyncThunk('fuel/getLastDispenses', async (_, thunkAPI) => {
  try {
    const resp: AxiosResponse<IDispense[]> = await baseUrl.get(
      'fuel/management/dispense/last?last=5',
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const createBatch = createAsyncThunk(
  'fuel/createBatch',
  async (state: IFuelBatchModal, thunkAPI) => {
    try {
      const req: ICreateBatchRequest = {
        fuelTankId: state.fuelTank.value,
        fuelType: state.fuelType.value,
        amount: state.amount,
        price: state.price,
        supplierId: state.supplier.value,
        date: formatDateWithoutTimezone(state.date),
        waybillRef: state.waybillRef || undefined,
        waybillLitres: state.waybillLitres || undefined,
        tankDipBeforeL: state.tankDipBefore || undefined,
        tankDipAfterL: state.tankDipAfter || undefined,
        sealNumbers: state.sealNumbers
          ? state.sealNumbers.split(',').map((s) => s.trim()).filter(Boolean)
          : undefined,
      }
      const resp: AxiosResponse<IBatch> = await baseUrl.post('fuel/management/batch', req)
      await thunkAPI.dispatch(getBatches(null))
      await thunkAPI.dispatch(getFuelDashboardAmounts())
      await thunkAPI.dispatch(getFuelDashboardTanks())
      await thunkAPI.dispatch(getFuelTanks({ page: 1, limit: 4 }))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelDashboardAmounts = createAsyncThunk(
  'fuel/getFuelDashboardAmounts',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelDashboardAmounts> = await baseUrl.get(
        'fuel/management/dashboard/info/amounts',
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelDashboardTanks = createAsyncThunk(
  'fuel/getFuelDashboardTanks',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelDashboardTanks> = await baseUrl.get(
        'fuel/management/dashboard/info/tanks',
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelLosses = createAsyncThunk(
  'fuel/getFuelLosses',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFuelLossResponse>> =
        await baseUrl.get('fuel/management/loss', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateFuelLossStatus = createAsyncThunk(
  'fuel/updateFuelLossStatus',
  async ({ id, body }: { id: number; body: IUpdateFuelLossCaseStatusRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelLossResponse> = await baseUrl.patch(
        `fuel/management/loss/${id}/status`,
        body,
      )
      toast.success('Loss case updated')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const approveFuelLossWriteOff = createAsyncThunk(
  'fuel/approveFuelLossWriteOff',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelLossResponse> = await baseUrl.post(
        `fuel/management/loss/${id}/approve-write-off`,
      )
      toast.success('Write-off approved')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addFuelTankWaterCheck = createAsyncThunk(
  'fuel/addFuelTankWaterCheck',
  async ({ id, body }: { id: number; body: IAddFuelTankWaterCheckRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelTank> = await baseUrl.post(
        `fuel/management/tank/${id}/water-check`,
        body,
      )
      toast.success('Water check recorded')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addFuelTankCleaning = createAsyncThunk(
  'fuel/addFuelTankCleaning',
  async ({ id, body }: { id: number; body: IAddFuelTankCleaningRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelTank> = await baseUrl.post(
        `fuel/management/tank/${id}/cleaning`,
        body,
      )
      toast.success('Tank cleaning recorded')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelMachines = createAsyncThunk(
  'fuel/getFuelMachines',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllFuelMachinesSingleResponse>> =
        await baseUrl.get('fuel/management/dashboard/machine', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelConsumptionOverTime = createAsyncThunk(
  'fuel/getFuelConsumptionOverTime',
  async (range: number, thunkAPI) => {
    range = range ?? 1
    try {
      const resp: AxiosResponse<IPaginatedResponse<IFuelConsumptionOverTime>> = await baseUrl.get(
        'fuel/management/dashboard/consumption',
        { params: { range } },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDispenses = createAsyncThunk('fuel/getDispense', async (params: any, thunkAPI) => {
  try {
    params = !params
      ? formatParams({
          page: 1,
          limit: 5,
          dateFrom: startOfWeek(new Date(), { weekStartsOn: 1 }),
          dateTo: endOfWeek(new Date(), { weekStartsOn: 1 }),
        })
      : formatParams(params)
    const resp: AxiosResponse<IPaginatedResponse<IBatch>> = await baseUrl.get(
      'fuel/management/dispense',
      { params },
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const createDispense = createAsyncThunk(
  'fuel/createDispense',
  async (state: IFuelDispenseModal, thunkAPI) => {
    try {
      const isDrum = state.type?.value === 3
      const req: ICreateDispenseRequest = {
        type: state.type?.value,
        machineId: state.machine?.value,
        fromFuelTankId: state.fromFuelTank?.value,
        toFuelTankId: state.toFuelTank?.value,
        fuelType: state.fuelType?.value,
        amount: state.amount,
        date: state.date?.toISOString(),
        odometer: state?.odometer,
        engineHours: state?.engineHours,
        dispenseMethod: isDrum ? FuelDispenseMethod.Drum : FuelDispenseMethod.Machine,
        signatureRef: isDrum ? state.signatureRef : undefined,
        issuedTo: state.issuedTo || undefined,
      }
      const resp: AxiosResponse<IDispense> = await baseUrl.post('fuel/management/dispense', req)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const importDispense = createAsyncThunk(
  'fuel/importDispense',
  async (file: File, thunkAPI) => {
    const formData = new FormData()
    formData.append('file', file)
    try {
      const resp: AxiosResponse = await baseUrl.post('fuel/management/dispense/import', formData)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const importFuelTasks = createAsyncThunk(
  'fuel/importFuelTasks',
  async (file: File, thunkAPI) => {
    const formData = new FormData()
    formData.append('file', file)
    try {
      const resp: AxiosResponse = await baseUrl.post('fuel/management/task/import', formData)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFuelTask = createAsyncThunk(
  'fuel/createFuelTask',
  async (state: IFuelTaskState, thunkAPI) => {
    const req: ICreateFuelTaskRequest = {
      ...state,
      breakdownType: state.breakdownType.value as 1 | 2,
    }
    try {
      const resp: AxiosResponse = await baseUrl.post('fuel/management/task', req)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelTanks = createAsyncThunk('fuel/getFuelTanks', async (params: any, thunkAPI) => {
  try {
    params = formatParams(params)
    const resp: AxiosResponse<IPaginatedResponse<IFuelTank>> = await baseUrl.get(
      'fuel/management/tank',
      { params },
    )
    resp.data.data = resp.data.data.map((tank) => {
      let sensor // TOOD type
      try {
        if (tank.sensor?.provider == 3) {
          sensor = {
            value: tank.sensor.develonVehicle.id + '_3',
            label: tank.sensor.develonVehicle.equipmentId,
          }
        } else if (tank.sensor?.provider == 2) {
          sensor = {
            value: tank.sensor.almaksVehicle.id + '_2',
            label: tank.sensor.almaksVehicle.name,
          }
        } else {
          sensor = tank.sensor
            ? {
                value: tank.sensor.frotcomVehicle.id + '_1',
                label:
                  tank.sensor.frotcomVehicle.manufacturer +
                  ' ' +
                  tank.sensor.frotcomVehicle.model +
                  ' ' +
                  tank.sensor.frotcomVehicle.licensePlate,
              }
            : null
        }
      } catch (error) {
        console.log(error)
      }

      return { ...tank, sensor }
    })

    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const createFuelTank = createAsyncThunk(
  'fuel/createFuelTank',
  async (state: ITanksModal, thunkAPI) => {
    try {
      const splited = state.sensor?.value.toString().split('_')
      const isBowser = state.machineClass === 'BOWSER'
      const machineId = state.machine?.value ?? state.machineId ?? state.machineLeagueId ?? null
      const req: IPostPatchFuelTankRequest = {
        name: state.name,
        capacity: state.capacity,
        sensorId: state.sensor?.value ? splited[0] : null,
        sensorProvider: state.sensor?.value ? Number(splited[1]) : null,
        tankClass: isBowser ? FuelTankClass.Bowser : FuelTankClass.Static,
        routeMeta: isBowser ? (state.routeMeta || null) : null,
        coverNote: isBowser ? (state.coverNote || null) : null,
        machineId: isBowser ? machineId : null,
      }
      const resp: AxiosResponse = await baseUrl.post('fuel/management/tank', req)
      await thunkAPI.dispatch(getFuelTanks(getParams({ location })))
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editFuelTank = createAsyncThunk(
  'fuel/editFuelTank',
  async (state: ITanksModal, thunkAPI) => {
    try {
      const splited = state.sensor?.value.toString().split('_')
      const isBowser = state.machineClass === 'BOWSER'
      const machineId = state.machine?.value ?? state.machineId ?? state.machineLeagueId ?? null
      const req: IPostPatchFuelTankRequest = {
        name: state.name,
        capacity: state.capacity,
        sensorId: state.sensor?.value ? splited[0] : null,
        sensorProvider: state.sensor?.value ? Number(splited[1]) : null,
        tankClass: isBowser ? FuelTankClass.Bowser : FuelTankClass.Static,
        routeMeta: isBowser ? (state.routeMeta || null) : null,
        coverNote: isBowser ? (state.coverNote || null) : null,
        machineId: isBowser ? machineId : null,
      }
      const resp: AxiosResponse = await baseUrl.patch('fuel/management/tank/' + state.id, req)
      await thunkAPI.dispatch(getFuelTanks(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteFuelTank = createAsyncThunk(
  'fuel/deleteFuelTank',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.delete('fuel/management/tank/' + id)
      await thunkAPI.dispatch(getFuelTanks(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelTypes = createAsyncThunk('fuel/getFuelTypes', async (params: any, thunkAPI) => {
  try {
    params = formatParams(params)
    const resp: AxiosResponse<IPaginatedResponse<IFuelType>> = await baseUrl.get(
      'fuel/management/type',
      { params },
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const createFuelType = createAsyncThunk(
  'fuel/createFuelType',
  async (state: IFuelTypesModal, thunkAPI) => {
    const req: IPostPatchFuelTypeRequest = {
      name: state.name,
      standardCost: state.standardCost,
      description: state.description,
    }
    try {
      const resp: AxiosResponse = await baseUrl.post('fuel/management/type', req)
      await thunkAPI.dispatch(getFuelTypes(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editFuelType = createAsyncThunk(
  'fuel/editFuelType',
  async (state: IFuelTypesModal, thunkAPI) => {
    const req: IPostPatchFuelTypeRequest = {
      name: state.name,
      standardCost: state.standardCost,
      description: state.description,
    }
    try {
      const resp: AxiosResponse = await baseUrl.patch('fuel/management/type/' + state.id, req)
      await thunkAPI.dispatch(getFuelTypes(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteFuelType = createAsyncThunk(
  'fuel/deleteFuelType',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.delete('fuel/management/type/' + id)
      await thunkAPI.dispatch(getFuelTypes(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelSuppliers = createAsyncThunk(
  'fuel/getFuelSuppliers',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFuelSupplier>> = await baseUrl.get(
        'fuel/management/supplier',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFuelSupplier = createAsyncThunk(
  'fuel/createFuelSupplier',
  async (state: IFuelSuppliersModal, thunkAPI) => {
    const req: IPostPatchFuelSupplierRequest = {
      name: state.name,
      phoneNumber: state.phoneNumber,
      email: state.email,
      address: state.address,
      vatId: state.vatId,
    }
    try {
      const resp: AxiosResponse = await baseUrl.post('fuel/management/supplier', req)
      await thunkAPI.dispatch(getFuelSuppliers(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editFuelSupplier = createAsyncThunk(
  'fuel/editFuelSupplier',
  async (state: IFuelSuppliersModal, thunkAPI) => {
    const req: IPostPatchFuelSupplierRequest = {
      name: state.name,
      phoneNumber: state.phoneNumber,
      email: state.email,
      address: state.address,
      vatId: state.vatId,
    }
    try {
      const resp: AxiosResponse = await baseUrl.patch('fuel/management/supplier/' + state.id, req)
      await thunkAPI.dispatch(getFuelSuppliers(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteFuelSupplier = createAsyncThunk(
  'fuel/deleteFuelSupplier',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.delete('fuel/management/supplier/' + id)
      await thunkAPI.dispatch(getFuelSuppliers(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const fuelManagementSlice = createSlice({
  name: 'fuelManagement',
  initialState,
  reducers: {
    clearFuelDashboard: (state) => {
      state.dashboardAmounts = null
      state.dashboardTanks = null
      state.dashboardAmountsLoading = false
      state.dashboardTanksLoading = false
    },
    clearTanks: (state) => {
      state.tanks = {
        data: [],
        totalCount: 0,
        totalPages: 0,
        isLoading: false,
      }
    },
    clearFuelMachines: (state) => {
      state.machines = {
        data: [],
        totalCount: 0,
        totalPages: 0,
      }
    },
    clearTypes: (state) => {
      state.tanks = {
        data: [],
        totalCount: 0,
        totalPages: 0,
        isLoading: false,
      }
    },
    clearFuelLosses: (state) => {
      state.fuelLosses = {
        data: [],
        totalCount: 0,
        totalPages: 0,
      }
    },
    clearFuelSuppliers: (state) => {
      state.suppliers = {
        totalCount: 0,
        totalPages: 0,
        data: [],
        isLoading: false,
      }
    },
    clearFuelData: (state) => {
      state.batches = {
        totalCount: 0,
        totalPages: 0,
        data: [],
        isLoading: false,
      }

      state.fuelConsumption = null
      state.alerts = []

      state.dispenses = {
        totalCount: 0,
        totalPages: 0,
        data: [],
        isLoading: false,
      }
    },
  },
  extraReducers: {
    [getFuelMachines.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFuelMachines.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllFuelMachinesSingleResponse> },
    ) => {
      state.isLoading = false
      state.machines = payload
    },
    [getFuelMachines.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFuelLosses.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFuelLosses.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFuelLossResponse> },
    ) => {
      state.isLoading = false
      state.fuelLosses = payload
    },
    [getFuelLosses.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFuelAlerts.pending.type]: (state) => {
      state.isLoadingAlerts = true
    },
    [getFuelAlerts.fulfilled.type]: (state, { payload }: { payload: IGetFuelAlertsResponse }) => {
      state.isLoadingAlerts = false
      state.alerts = payload.alerts
    },
    [getFuelAlerts.rejected.type]: (state, { payload }) => {
      state.isLoadingAlerts = false
      handleErrors(payload)
    },
    [getFuelConsumptionOverTime.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFuelConsumptionOverTime.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.fuelConsumption = payload
    },
    [getFuelConsumptionOverTime.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFuelDashboardAmounts.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFuelDashboardAmounts.fulfilled.type]: (
      state,
      { payload }: { payload: IFuelDashboardAmounts },
    ) => {
      state.isLoading = false
      state.dashboardAmounts = payload
    },
    [getFuelDashboardAmounts.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFuelDashboardTanks.pending.type]: (state) => {
      state.dashboardTanksLoading = true
    },
    [getFuelDashboardTanks.fulfilled.type]: (
      state,
      { payload }: { payload: IFuelDashboardTanks },
    ) => {
      state.dashboardTanksLoading = false
      state.dashboardTanks = payload
    },
    [getFuelDashboardTanks.rejected.type]: (state, { payload }) => {
      state.dashboardTanksLoading = false
      handleErrors(payload)
    },
    [deleteFuelTank.pending.type]: (state) => {
      state.tanks.isLoading = true
    },
    [deleteFuelTank.fulfilled.type]: (state) => {
      state.tanks.isLoading = false
      toast.success('Fuel tank deleted successfully.')
    },
    [deleteFuelTank.rejected.type]: (state, { payload }) => {
      state.tanks.isLoading = false
      handleErrors(payload)
    },
    [editFuelTank.pending.type]: (state) => {
      state.tanks.isLoading = true
    },
    [editFuelTank.fulfilled.type]: (state) => {
      state.tanks.isLoading = false
      toast.success('Fuel tank edited successfully.')
    },
    [editFuelTank.rejected.type]: (state, { payload }) => {
      state.tanks.isLoading = false
      handleErrors(payload)
    },
    [createFuelTank.pending.type]: (state) => {
      state.tanks.isLoading = true
    },
    [createFuelTank.fulfilled.type]: (state) => {
      state.tanks.isLoading = false
      toast.success('Fuel tank added successfully.')
    },
    [createFuelTank.rejected.type]: (state, { payload }) => {
      state.tanks.isLoading = false
      handleErrors(payload)
    },
    [getFuelTanks.pending.type]: (state) => {
      state.tanks.isLoading = true
    },
    [getFuelTanks.fulfilled.type]: (state, { payload }) => {
      state.tanks.isLoading = false
      state.tanks.totalCount = payload.totalCount
      state.tanks.totalPages = payload.totalPages
      state.tanks.data = payload.data
    },
    [getFuelTanks.rejected.type]: (state, { payload }) => {
      state.tanks.isLoading = false
      handleErrors(payload)
    },
    [deleteFuelType.pending.type]: (state) => {
      state.types.isLoading = true
    },
    [deleteFuelType.fulfilled.type]: (state) => {
      state.types.isLoading = false
      toast.success('Fuel type deleted successfully.')
    },
    [deleteFuelType.rejected.type]: (state, { payload }) => {
      state.types.isLoading = false
      handleErrors(payload)
    },
    [editFuelType.pending.type]: (state) => {
      state.types.isLoading = true
    },
    [editFuelType.fulfilled.type]: (state) => {
      state.types.isLoading = false
      toast.success('Fuel type edited successfully.')
    },
    [editFuelType.rejected.type]: (state, { payload }) => {
      state.types.isLoading = false
      handleErrors(payload)
    },
    [createFuelType.pending.type]: (state) => {
      state.types.isLoading = true
    },
    [createFuelType.fulfilled.type]: (state) => {
      state.types.isLoading = false
      toast.success('Fuel type added successfully.')
    },
    [createFuelType.rejected.type]: (state, { payload }) => {
      state.types.isLoading = false
      handleErrors(payload)
    },
    [getFuelTypes.pending.type]: (state) => {
      state.types.isLoading = true
    },
    [getFuelTypes.fulfilled.type]: (state, { payload }) => {
      state.types.isLoading = false
      state.types.totalCount = payload.totalCount
      state.types.totalPages = payload.totalPages
      state.types.data = payload.data
    },
    [getFuelTypes.rejected.type]: (state, { payload }) => {
      state.types.isLoading = false
      handleErrors(payload)
    },
    [getFuelSuppliers.pending.type]: (state) => {
      state.suppliers.isLoading = true
    },
    [getFuelSuppliers.fulfilled.type]: (state, { payload }) => {
      state.suppliers.isLoading = false
      state.suppliers.totalCount = payload.totalCount
      state.suppliers.totalPages = payload.totalPages
      state.suppliers.data = payload.data
    },
    [getFuelSuppliers.rejected.type]: (state, { payload }) => {
      state.suppliers.isLoading = false
      handleErrors(payload)
    },
    [createFuelSupplier.pending.type]: (state) => {
      state.suppliers.isLoading = true
    },
    [createFuelSupplier.fulfilled.type]: (state) => {
      state.suppliers.isLoading = false
      toast.success('Fuel supplier added successfully.')
    },
    [createFuelSupplier.rejected.type]: (state, { payload }) => {
      state.suppliers.isLoading = false
      handleErrors(payload)
    },
    [editFuelSupplier.pending.type]: (state) => {
      state.suppliers.isLoading = true
    },
    [editFuelSupplier.fulfilled.type]: (state) => {
      state.suppliers.isLoading = false
      toast.success('Fuel supplier edited successfully.')
    },
    [editFuelSupplier.rejected.type]: (state, { payload }) => {
      state.suppliers.isLoading = false
      handleErrors(payload)
    },
    [deleteFuelSupplier.pending.type]: (state) => {
      state.suppliers.isLoading = true
    },
    [deleteFuelSupplier.fulfilled.type]: (state) => {
      state.suppliers.isLoading = false
      toast.success('Fuel supplier deleted successfully.')
    },
    [deleteFuelSupplier.rejected.type]: (state, { payload }) => {
      state.suppliers.isLoading = false
      handleErrors(payload)
    },
    [getBatches.pending.type]: (state) => {
      state.batches.isLoading = true
    },
    [getBatches.fulfilled.type]: (state, { payload }) => {
      state.batches.isLoading = false
      state.batches.totalCount = payload.totalCount
      state.batches.totalPages = payload.totalPages
      state.batches.data = payload.data
    },
    [getBatches.rejected.type]: (state, { payload }) => {
      state.batches.isLoading = false
      handleErrors(payload)
    },
    [getLastBatches.pending.type]: (state) => {
      state.batches.isLoading = true
    },
    [getLastBatches.fulfilled.type]: (state, { payload }) => {
      state.batches.isLoading = false
      state.batches.data = payload
    },
    [getLastBatches.rejected.type]: (state, { payload }) => {
      state.batches.isLoading = false
      handleErrors(payload)
    },
    [getLastDispenses.pending.type]: (state) => {
      state.dispenses.isLoading = true
    },
    [getLastDispenses.fulfilled.type]: (state, { payload }) => {
      state.dispenses.isLoading = false
      state.dispenses.data = payload
    },
    [getLastDispenses.rejected.type]: (state, { payload }) => {
      state.dispenses.isLoading = false
      handleErrors(payload)
    },
    [getDispenses.pending.type]: (state) => {
      state.dispenses.isLoading = true
    },
    [getDispenses.fulfilled.type]: (state, { payload }) => {
      state.dispenses.isLoading = false
      state.dispenses.totalCount = payload.totalCount
      state.dispenses.totalPages = payload.totalPages
      state.dispenses.data = payload.data
    },
    [getDispenses.rejected.type]: (state, { payload }) => {
      state.dispenses.isLoading = false
      handleErrors(payload)
    },
    [createBatch.pending.type]: (state) => {
      state.batches.isLoading = true
    },
    [createBatch.fulfilled.type]: (state) => {
      state.batches.isLoading = false
      toast.success('Batch added')
    },
    [createBatch.rejected.type]: (state, { payload }) => {
      state.batches.isLoading = false
      handleErrors(payload)
    },
    [createDispense.pending.type]: (state) => {
      state.dispenses.isLoading = true
    },
    [createDispense.fulfilled.type]: (state, { payload }: { payload: IDispense }) => {
      state.dispenses.isLoading = false
      toast.success('Dispense added')
      if (state.dispenses.data.length < 5) {
        state.dispenses.data = [...state.dispenses.data, payload]
      }
    },
    [createDispense.rejected.type]: (state, { payload }) => {
      state.dispenses.isLoading = false
      handleErrors(payload)
    },
    [importDispense.pending.type]: (state) => {
      state.dispenses.isLoading = true
    },
    [importDispense.fulfilled.type]: (state) => {
      state.dispenses.isLoading = false
      toast.success('Fuel dispenses imported successfully.')
      // TOOD Refresh dispense list
    },
    [importDispense.rejected.type]: (state, { payload }) => {
      state.dispenses.isLoading = false
      handleErrors(payload)
    },
    [importFuelTasks.pending.type]: (state) => {
      state.tasks.isLoading = true
    },
    [importFuelTasks.fulfilled.type]: (state) => {
      state.tasks.isLoading = false
      toast.success('Fuel tasks imported successfully.')
    },
    [importFuelTasks.rejected.type]: (state, { payload }) => {
      state.tasks.isLoading = false
      handleErrors(payload)
    },
    [createFuelTask.pending.type]: (state) => {
      state.tasks.isLoading = true
    },
    [createFuelTask.fulfilled.type]: (state) => {
      state.tasks.isLoading = false
      toast.success('Fuel task created successfully.')
    },
    [createFuelTask.rejected.type]: (state, { payload }) => {
      state.tasks.isLoading = false
      handleErrors(payload)
    },
  },
})
export const {
  clearTanks,
  clearTypes,
  clearFuelData,
  clearFuelLosses,
  clearFuelMachines,
  clearFuelSuppliers,
  clearFuelDashboard,
} = fuelManagementSlice.actions
export default fuelManagementSlice.reducer
